<script setup lang="ts">
import { computed, watch } from 'vue'

import BaseModal from '@/components/BaseModal.vue'
import { logService, LOG_CATEGORIES } from '@/services/logService'
import { useSearchWorkspaceStore } from '@/stores/searchWorkspaceStore'
import { useWorkspaceStore } from '@/stores/workspaceStore'

defineOptions({ name: 'TourDeleteModal' })

type TourDeleteModalTour = {
  clientId: string
  name: string
  isPinned?: boolean
  workspaces?: Array<{ clientId: string; name: string }>
}

const props = withDefaults(
  defineProps<{
    open: boolean
    tour: TourDeleteModalTour | null
    tourName?: string | null
    deleting?: boolean
    includeCurrentCollection?: boolean
  }>(),
  {
    tourName: null,
    deleting: false,
    includeCurrentCollection: true,
  },
)

const emit = defineEmits<{
  close: []
  confirm: []
}>()

const searchWorkspaceStore = useSearchWorkspaceStore()
const workspaceStore = useWorkspaceStore()

type FeaturedCollection = {
  name: string
  isCurrent: boolean
  isPinned: boolean
}

const PINNED_COLLECTION_ID = '__pinned__'
const PINNED_COLLECTION_NAME = 'Pinned'

const isPinnedCollectionName = (name: string | null | undefined) =>
  name?.trim().toLowerCase() === 'pinned'

const isCurrentPinnedContext = computed(
  () =>
    (workspaceStore.isSystem === true && workspaceStore.systemKey === 'pinned') ||
    isPinnedCollectionName(workspaceStore.name),
)

const displayTourName = computed(() => props.tourName?.trim() || props.tour?.name || 'tour')

const addCollection = (
  collectionsById: Map<string, FeaturedCollection>,
  id: string,
  name: string,
  options: { isCurrent?: boolean; isPinned?: boolean } = {},
) => {
  const trimmedName = name.trim()
  if (!id || !trimmedName) return
  const normalizedId = options.isPinned ? PINNED_COLLECTION_ID : id
  const normalizedName = options.isPinned ? PINNED_COLLECTION_NAME : trimmedName
  const existing = collectionsById.get(normalizedId)
  collectionsById.set(normalizedId, {
    name: existing?.name ?? normalizedName,
    isCurrent: existing?.isCurrent === true || options.isCurrent === true,
    isPinned: existing?.isPinned === true || options.isPinned === true,
  })
}

const featuredCollectionLabels = computed(() => {
  const tour = props.tour
  if (!tour) return []
  const collectionsById = new Map<string, FeaturedCollection>()

  if (props.includeCurrentCollection && workspaceStore.clientId && workspaceStore.name?.trim()) {
    addCollection(collectionsById, workspaceStore.clientId, workspaceStore.name, {
      isCurrent: true,
      isPinned: isCurrentPinnedContext.value,
    })
  }

  for (const workspace of tour.workspaces ?? []) {
    addCollection(collectionsById, workspace.clientId, workspace.name, {
      isPinned: isPinnedCollectionName(workspace.name),
    })
  }

  for (const workspace of searchWorkspaceStore.allWorkspaces) {
    const containsTour = workspace.tours.some(
      (workspaceTour) => workspaceTour.clientId === tour.clientId,
    )
    if (containsTour) {
      addCollection(collectionsById, workspace.clientId, workspace.name, {
        isPinned: isPinnedCollectionName(workspace.name),
      })
    }
  }

  if (tour.isPinned || isCurrentPinnedContext.value) {
    addCollection(collectionsById, PINNED_COLLECTION_ID, PINNED_COLLECTION_NAME, {
      isCurrent: props.includeCurrentCollection && isCurrentPinnedContext.value,
      isPinned: true,
    })
  }

  const formatLabel = (collection: FeaturedCollection) =>
    `${collection.name}${collection.isCurrent ? ' (current)' : ''}`

  const collections = Array.from(collectionsById.values())
  const currentCollections = collections.filter((collection) => collection.isCurrent)
  const regularCollections = collections.filter(
    (collection) => !collection.isCurrent && !collection.isPinned,
  )
  const pinnedCollections = collections.filter(
    (collection) => !collection.isCurrent && collection.isPinned,
  )

  return [...currentCollections, ...regularCollections, ...pinnedCollections].map(formatLabel)
})

watch(
  () => props.open,
  async (open) => {
    if (!open || !props.tour) return
    try {
      await searchWorkspaceStore.ensureWorkspacesLoaded()
    } catch (error) {
      logService.warn(LOG_CATEGORIES.API, 'Failed to refresh collection list before tour delete', {
        error,
        tourId: props.tour.clientId,
      })
    }
  },
)
</script>

<template>
  <BaseModal
    :is-open="open"
    :title="`Delete ${displayTourName}?`"
    size="sm"
    :show-footer="true"
    role="alertdialog"
    @close="emit('close')"
  >
    <div class="space-y-3 text-sm text-gray-700">
      <p v-if="featuredCollectionLabels.length > 0">
        This track will be permanently deleted and removed from these collections:
      </p>
      <p v-else>This track will be permanently deleted.</p>
      <ul v-if="featuredCollectionLabels.length > 0" class="list-disc space-y-1 pl-5">
        <li v-for="collection in featuredCollectionLabels" :key="collection">
          {{ collection }}
        </li>
      </ul>
      <p>This action cannot be undone.</p>
    </div>

    <template #footer>
      <button
        type="button"
        class="px-3 py-1.5 text-sm text-gray-600 transition-colors hover:cursor-pointer hover:bg-gray-100 rounded-md"
        @click="emit('close')"
      >
        Cancel
      </button>
      <button
        type="button"
        :disabled="deleting"
        class="rounded-md bg-red-600 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:cursor-pointer hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
        @click="emit('confirm')"
      >
        {{ deleting ? 'Deleting...' : 'Delete' }}
      </button>
    </template>
  </BaseModal>
</template>
